import axios, { AxiosError, AxiosResponse } from 'axios';
import { Tenant, TenantUserData, TenantPlan, TenantStatus } from './types';
import ApiErrorHandler from '../api-error-handler';
import TenantErrorHandler from './tenant-error-handler';
import { mockTenants } from './mock-tenants';
import { tenantManager } from './tenant-manager';

// Alias de type pour compatibilité avec le code existant
type TenantType = Tenant;

// Define TenantUser interface if not already in types.ts
interface TenantUser {
  id: string;
  email: string;
  username: string;
  first_name?: string;
  last_name?: string;
  role: string;
  status: 'active' | 'inactive' | 'pending';
  last_login?: string;
  invitedAt?: string;
  joinedAt?: string;
  slug?: string;
}

// Helper types for API responses
interface TenantApiError {
  message: string;
  code?: string;
  details?: any;
}

interface TenantInviteRequest {
  email: string;
  role?: string;
  send_email?: boolean;
}

interface TenantApiResponse<T> {
  success: boolean;
  message: string;
  data?: T;
}

// Audit action types
enum AuditActionType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  INVITE = 'invite',
  LOGIN = 'login',
  READ = 'read'
}

// TenantAuditService class implementation if it doesn't exist
class TenantAuditService {
  async logAction(actionData: {
    tenantId: string;
    userId: string;
    action: AuditActionType;
    resource: string;
    resourceId: string;
    status: 'success' | 'failure';
    details?: any;
  }): Promise<void> {
    try {
      console.log('🧾 Tenant Audit Log:', actionData);
      // Implementation would go here - for now just logging
    } catch (error) {
      console.error('❌ Error logging tenant audit:', error);
    }
  }
}

// Create an instance of the audit service
const tenantAuditService = new TenantAuditService();

// Configure axios instance
const axiosInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL || '/api',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Helper functions
async function getAuthToken(): Promise<string | null> {
  try {
    const tokenModule = await import('../auth/token-manager');
    const token = tokenModule.TokenManager.getAccessToken() || '';
    const isAuthenticated = tokenModule.TokenManager.isAuthenticated();
    if (!isAuthenticated || !token) {
      return null;
    }
    return token;
  } catch (error) {
    console.error('❌ Error retrieving authentication token:', error);
    return null;
  }
}

function handleApiError(error: unknown): never {
  console.error('❌ TenantAPI - API Error:', error);
  if (error instanceof AxiosError) {
    const message = error.response?.data?.message || error.message;
    throw new Error(`API Error: ${message}`);
  }
  throw error;
}

/**
 * TenantAPI provides methods for interacting with tenant-related functionalities
 * including retrieval, creation, updating, and management of tenants and their users.
 */
const tenantAPI = {
  // Cache for tenant users to reduce API calls
  userCache: {} as {
    [tenantId: string]: {
      users: TenantUser[];
      timestamp: number;
      isFallback?: boolean;
    };
  },
  
  // Cache TTL in milliseconds (10 minutes)
  CACHE_TTL: 10 * 60 * 1000,
  
  /**
   * Récupère tous les tenants accessibles à l'utilisateur courant (admin ou staff)
   * @returns Liste des tenants
   */
  async getAllTenants(): Promise<Tenant[]> {
    const endpoint = 'tenants/';
    try {
      const token = await getAuthToken();
      if (!token) {
        console.warn('🔴 TenantAPI - Utilisateur non authentifié ou token manquant, impossible de récupérer tous les tenants');
        return [];
      }
      
      const response = await axiosInstance.get(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        withCredentials: true,
        timeout: 8000,
      });
      
      ApiErrorHandler.getInstance().recordApiSuccess(endpoint, response.status);
      
      // Gérer différents formats de réponse
      let tenantsArray: any[] = [];
      
      if (response.data && Array.isArray(response.data.results)) {
        tenantsArray = response.data.results;
      } else if (response.data && Array.isArray(response.data.tenants)) {
        tenantsArray = response.data.tenants;
      } else if (response.data && Array.isArray(response.data.organizations)) {
        tenantsArray = response.data.organizations;
      } else if (Array.isArray(response.data)) {
        tenantsArray = response.data;
      } else if (response.data && typeof response.data === 'object') {
        // Sometimes the API might return a single tenant as an object
        if (response.data.id && response.data.name) {
          tenantsArray = [response.data];
        } else {
          tenantsArray = [];
          console.warn(`[TenantAPI] Format de réponse inattendu pour l'endpoint ${endpoint}:`, response.data);
        }
      } else {
        tenantsArray = [];
        console.warn(`[TenantAPI] Format de réponse inattendu pour l'endpoint ${endpoint}:`, response.data);
      }
      
      // Toujours retourner un tableau de tenants valides
      return tenantsArray.filter(t => t && t.id) as Tenant[];
    } catch (error) {
      console.error('Error fetching all tenants:', error);
      if (error instanceof AxiosError) {
        ApiErrorHandler.getInstance().recordApiError(error);
      }
      
      // En mode développement, retourner des données mockées
      if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
        console.log('🔄 TenantAPI - Returning mock tenants data');
        return mockTenants;
      }
      
      return [];
    }
  },

  /**
   * Récupère les tenants de l'utilisateur connecté à partir des différents endpoints possibles
   * @returns Une liste de tenants
   */
  async getUserTenants(): Promise<Tenant[]> {
    // Liste d'endpoints à essayer (par ordre de priorité)
    const endpoints = [
      'tenants/me',           // Endpoint principal
      'tenants/user',         // Endpoint alternatif
      'users/me/tenants',     // Endpoint alternatif orienté utilisateur
      'organizations/me',      // Nom alternatif
      'organizations/user',   // Nom alternatif orienté utilisateur
    ];
    
    const token = await getAuthToken();
    if (!token) {
      console.warn('🔴 TenantAPI - Utilisateur non authentifié ou token manquant, utilisation de données mockées');
      return process.env.NODE_ENV !== 'production' ? mockTenants : [];
    }
    
    // Essayer chaque endpoint jusqu'à ce qu'un fonctionne
    const errors: Record<string, any> = {};
    
    for (const endpoint of endpoints) {
      try {
        console.log(`🔄 TenantAPI - Tentative de récupération des tenants via l'endpoint: ${endpoint}`);
        
        const response = await axiosInstance.get(endpoint, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          withCredentials: true,
          timeout: 5000, // Timeout plus court pour passer rapidement à l'endpoint suivant en cas d'erreur
        });
        
        // Enregistrer le succès de la requête
        ApiErrorHandler.getInstance().recordApiSuccess(endpoint, response.status);
        
        // Gérer différents formats de réponse
        let tenantsArray: any[] = [];
        
        if (response.data && Array.isArray(response.data.results)) {
          tenantsArray = response.data.results;
          console.log(`[TenantAPI] Extraction depuis 'results':`, tenantsArray.length);
        } else if (response.data && Array.isArray(response.data.tenants)) {
          tenantsArray = response.data.tenants;
          console.log(`[TenantAPI] Extraction depuis 'tenants':`, tenantsArray.length);
        } else if (response.data && Array.isArray(response.data.organizations)) {
          tenantsArray = response.data.organizations;
          console.log(`[TenantAPI] Extraction depuis 'organizations':`, tenantsArray.length);
        } else if (Array.isArray(response.data)) {
          tenantsArray = response.data;
          console.log(`[TenantAPI] Extraction directe array:`, tenantsArray.length);
        } else if (response.data && typeof response.data === 'object') {
          // Parfois l'API peut retourner un tenant unique sous forme d'objet
          if (response.data.id && response.data.name) {
            tenantsArray = [response.data];
            console.log(`[TenantAPI] Extraction objet unique:`, tenantsArray);
          } else {
            console.warn(`[TenantAPI] Format de réponse inattendu pour l'endpoint ${endpoint}:`, response.data);
          }
        }
        
        // Filtrer pour ne garder que les tenants valides
        const validTenants = tenantsArray.filter(t => t && t.id) as Tenant[];
        
        if (validTenants.length > 0) {
          console.log(`✅ TenantAPI - Récupération réussie via ${endpoint}: ${validTenants.length} tenant(s)`);
          return validTenants;
        } else {
          console.warn(`⚠️ TenantAPI - Aucun tenant valide trouvé via ${endpoint}`);
        }
      } catch (error) {
        console.error(`❌ TenantAPI - Échec de récupération via ${endpoint}:`, error);
        errors[endpoint] = error;
        
        // Enregistrer l'erreur
        if (error instanceof AxiosError) {
          ApiErrorHandler.getInstance().recordApiError(error);
        }
      }
    }
    
    // Si on arrive ici, c'est qu'aucun endpoint n'a fonctionné
    console.error('💥 TenantAPI - Échec de récupération des tenants utilisateur après plusieurs tentatives:', errors);
    
    // En mode développement, retourner des données mockées
    if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
      console.log('🔄 TenantAPI - Returning mock tenants data');
      return mockTenants;
    }
    
    return [];
  },
  
  /**
   * Récupère les données utilisateur liées au tenant
   * @returns Données utilisateur du tenant ou null si non disponible
   */
  async getUserTenantData(): Promise<TenantUserData | null> {
    try {
      const token = await getAuthToken();
      if (!token) {
        console.warn('🔴 TenantAPI - Utilisateur non authentifié ou token manquant, impossible de récupérer les données utilisateur');
        return null;
      }
      
      const endpoint = 'users/me/tenants/';
      const response = await axiosInstance.get(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        withCredentials: true,
      });
      
      ApiErrorHandler.getInstance().recordApiSuccess(endpoint, response.status);
      
      if (response.data && response.data.tenant_id) {
        return {
          tenantId: response.data.tenant_id,
          tenantRole: response.data.tenant_role || 'user',
          tenants: response.data.tenants || [],
        };
      } else if (response.data && response.data.tenantId) {
        return {
          tenantId: response.data.tenantId,
          tenantRole: response.data.tenantRole || 'user',
          tenants: response.data.tenants || [],
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching user tenant data:', error);
      if (error instanceof AxiosError) {
        ApiErrorHandler.getInstance().recordApiError(error);
      }
      return null;
    }
  },
  
  /**
   * Récupère un tenant par son ID et vérifie que l'utilisateur y a accès
   * @param tenantId ID du tenant à récupérer
   * @returns Le tenant ou null si non trouvé ou accès non autorisé
   */
  async getTenant(tenantId: string): Promise<Tenant | null> {
    if (!tenantId) {
      console.warn('🔴 TenantAPI - ID du tenant non fourni');
      return null;
    }
    
    // Vérifier si le tenant est accessible
    if (!tenantManager.isTenantAccessible(tenantId)) {
      console.warn(`🔴 TenantAPI - Tenant ${tenantId} non accessible`);
      return null;
    }
    
    try {
      const token = await getAuthToken();
      if (!token) {
        console.warn('🔴 TenantAPI - Utilisateur non authentifié ou token manquant');
        return null;
      }
      
      const endpoint = `tenants/${tenantId}/`;
      const response = await axiosInstance.get(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        withCredentials: true,
      });
      
      ApiErrorHandler.getInstance().recordApiSuccess(endpoint, response.status);
      
      if (response.data && response.data.id) {
        return response.data;
      }
      
      return null;
    } catch (error) {
      console.error(`Error fetching tenant ${tenantId}:`, error);
      if (error instanceof AxiosError) {
        ApiErrorHandler.getInstance().recordApiError(error);
      }
      
      // En mode développement, essayer de trouver le tenant dans les données mockées
      if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
        const mockTenant = mockTenants.find(t => t.id === tenantId);
        if (mockTenant) {
          console.log(`🔄 TenantAPI - Returning mock tenant for ID ${tenantId}`);
          return mockTenant;
        }
      }
      
      return null;
    }
  },
  
  /**
   * Ajoute le contexte tenant aux paramètres
   * @param params Paramètres à enrichir
   * @returns Paramètres avec le contexte tenant
   */
  addTenantContext<T>(params: T): T & { tenantId: string } {
    if (!tenantManager) {
      console.error('TenantManager non disponible, impossible d\'ajouter le contexte tenant');
      return { ...params, tenantId: '' } as T & { tenantId: string };
    }
    return this.tenantManager.addTenantContext(params);
  },

  filterDataByTenant<T extends { tenantId?: string }>(data: T[]): T[] {
    if (!data || !Array.isArray(data)) {
      return [];
    }

    const currentTenantId = tenantManager.getCurrentTenantId();
    if (!currentTenantId) {
      return data;
    }

    return data.filter(item => !item.tenantId || item.tenantId === currentTenantId);
  },

  /**
   * Invite un utilisateur à rejoindre un tenant
   * @param tenantId ID du tenant
   * @param email Email de l'utilisateur à inviter
   * @param role Rôle de l'utilisateur (optionnel, par défaut 'user')
   * @returns Résultat de l'opération
   */
  /**
   * Supprime un tenant par son ID
   * @param tenantId ID du tenant à supprimer
   * @returns Résultat de l'opération
   */
  async deleteTenant(tenantId: string): Promise<{ success: boolean; message: string }> {
    // Validation des paramètres
    if (!tenantId) {
      console.warn('deleteTenant: ID du tenant manquant');
      return {
        success: false,
        message: 'L\'ID du tenant est requis pour la suppression'
      };
    }

    try {
      // Vérifier si l'utilisateur a les droits d'accès au tenant
      if (!tenantManager.isTenantAccessible(tenantId)) {
        console.warn(`🚫 TenantAPI: Tenant non accessible pour suppression: ${tenantId}`);
        return {
          success: false,
          message: `Vous n'avez pas les droits nécessaires pour supprimer cette organisation.`
        };
      }

      // Préparer les paramètres de l'appel API
      const endpoint = `tenants/${tenantId}/`;
      const token = await import('../auth/token-manager').then(mod => mod.TokenManager.getAccessToken() || '');
      
      // Appel à l'API pour supprimer le tenant
      const response = await axiosInstance.delete(endpoint, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        withCredentials: true,
      });
      
      // Enregistrer le succès de la requête
      ApiErrorHandler.getInstance().recordApiSuccess(endpoint, response.status);
      
      // Journaliser l'action de suppression
      await tenantAuditService.logAction({
        tenantId,
        userId: 'current-user',
        action: AuditActionType.DELETE,
        resource: 'tenant',
        resourceId: tenantId,
        status: 'success',
        details: { deleted_at: new Date().toISOString() },
      });
      
      return { 
        success: true, 
        message: response.data?.message || 'Organisation supprimée avec succès' 
      };
    } catch (error: unknown) {
      console.error('Erreur lors de la suppression du tenant:', error);
      
      // Enregistrer l'erreur
      if (error instanceof AxiosError) {
        const endpoint = `tenants/${tenantId}/`;
        ApiErrorHandler.getInstance().recordApiError(error);
        
        // Journaliser l'échec de la suppression
        await tenantAuditService.logAction({
          tenantId,
          userId: 'current-user',
          action: AuditActionType.DELETE,
          resource: 'tenant',
          resourceId: tenantId,
          status: 'failure',
          details: { error: error instanceof Error ? error.message : String(error) },
        }).catch(e => console.error('Failed to log audit event:', e));
      }
      
      // En mode développement, simuler une suppression réussie
      if (process.env.NODE_ENV !== 'production') {
        console.log(`🔍 Mode développement: Simulation de suppression du tenant ${tenantId}`);
        return { 
          success: true, 
          message: 'Organisation supprimée avec succès (simulé)' 
        };
      }
      
      // Fournir un message d'erreur convivial
      let errorMessage = 'Échec de la suppression de l\'organisation';
      if (error instanceof AxiosError && error.response) {
        errorMessage = ApiErrorHandler.getErrorMessage(error);
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      return { success: false, message: errorMessage };
    }
  },

  /**
   * Crée un nouveau tenant/organisation
   * @param tenantData Données du tenant à créer
   * @param userId ID de l'utilisateur propriétaire (optionnel)
   * @returns Le tenant créé
   */
  async createTenant(tenantData: Partial<Tenant>, userId?: string): Promise<Tenant> {
    try {
      // Validation des données minimales requises
      if (!tenantData.name) {
        throw new Error('Le nom du tenant est requis');
      }
      
      // Générer un slug si non fourni
      if (!tenantData.slug) {
        tenantData.slug = tenantData.name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '') +
          '-' +
          Math.random().toString(36).substring(2, 10);
      }
      
      // Préparer les données à envoyer
      const payload = {
        ...tenantData,
        owner_id: userId || undefined
      };
      
      // Obtenir le token d'authentification
      const token = await getAuthToken();
      if (!token) {
        throw new Error('Vous devez être connecté pour créer une organisation');
      }
      
      // Appel API pour créer le tenant
      const endpoint = 'tenants/';
      const response = await axiosInstance.post(endpoint, payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        withCredentials: true,
      });
      
      // Enregistrer le succès de la requête
      ApiErrorHandler.getInstance().recordApiSuccess(endpoint, response.status);
      
      // Journaliser la création du tenant
      await tenantAuditService.logAction({
        tenantId: response.data.id,
        userId: userId || 'current-user',
        action: AuditActionType.CREATE,
        resource: 'tenant',
        resourceId: response.data.id,
        status: 'success',
        details: { name: tenantData.name, plan: tenantData.plan }
      }).catch(e => console.error('Failed to log audit event:', e));
      
      // Retourner le tenant créé
      return response.data;
    } catch (error) {
      console.error('❌ Error creating tenant:', error);
      
      // Enregistrer l'erreur
      if (error instanceof AxiosError) {
        ApiErrorHandler.getInstance().recordApiError(error);
      }
      
      // En mode développement, créer un tenant mocké
      if (process.env.NODE_ENV !== 'production') {
        console.log('🔄 Creating mock tenant in development mode');
        const mockTenant: Tenant = {
          id: `mock-tenant-${Date.now()}`,
          name: tenantData.name || 'Mock Tenant',
          slug: tenantData.slug || `mock-tenant-${Date.now()}`,
          plan: tenantData.plan as TenantPlan || 'free',
          status: 'active' as TenantStatus,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          settings: tenantData.settings || {},
          domain: tenantData.domain || '',
          features: {
            advancedThreatDetection: false,
            vulnerabilityScanning: false,
            assetDiscovery: true,
            complianceReporting: false,
            apiAccess: false,
            customIntegrations: false
          }
        };
        return mockTenant;
      }
      
      // Propager l'erreur
      throw error;
    }
  },

  /**
   * Définit un tenant comme tenant par défaut pour l'utilisateur courant
   * @param tenantId ID du tenant à définir comme défaut
   * @returns Résultat de l'opération
   */
  async setDefaultTenant(tenantId: string): Promise<{ success: boolean; message: string }> {
    // Validation des paramètres
    if (!tenantId) {
      console.warn('setDefaultTenant: paramètre invalide', { tenantId });
      return {
        success: false,
        message: 'L\'ID du tenant est requis pour définir le tenant par défaut'
      };
    }

    try {
      // Vérifier si le tenant est accessible
      if (!tenantManager.isTenantAccessible(tenantId)) {
        console.warn(`👤 TenantAPI: Tenant non accessible pour définition par défaut: ${tenantId}`);
        return {
          success: false,
          message: `Vous n'avez pas les droits nécessaires pour définir ce tenant comme tenant par défaut.`
        };
      }

      const endpoint = `tenants/${tenantId}/set-default/`;
      const token = await getAuthToken();
      
      if (!token) {
        console.warn('🔴 TenantAPI - Utilisateur non authentifié, impossible de définir le tenant par défaut');
        return {
          success: false,
          message: 'Vous devez être connecté pour définir un tenant par défaut'
        };
      }

      const response = await axiosInstance.post(endpoint, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        withCredentials: true,
      });
      
      // Enregistrer le succès de la requête
      ApiErrorHandler.getInstance().recordApiSuccess(endpoint, response.status);
      
      // Journaliser l'action
      await tenantAuditService.logAction({
        tenantId,
        userId: 'current-user',
        action: AuditActionType.UPDATE,
        resource: 'tenant_default',
        resourceId: tenantId,
        status: 'success',
        details: { action: 'set_default' },
      });

      return { 
        success: true, 
        message: response.data?.message || 'Tenant défini comme tenant par défaut avec succès' 
      };
    } catch (error: unknown) {
      console.error('❌ Error setting default tenant:', error);
      
      // Enregistrer l'erreur
      if (error instanceof AxiosError) {
        const endpoint = `tenants/${tenantId}/set-default/`;
        ApiErrorHandler.getInstance().recordApiError(error);
        
        // Journaliser l'échec
        await tenantAuditService.logAction({
          tenantId,
          userId: 'current-user',
          action: AuditActionType.UPDATE,
          resource: 'tenant_default',
          resourceId: tenantId,
          status: 'failure',
          details: { error: error instanceof Error ? error.message : String(error) },
        }).catch(e => console.error('Failed to log audit event:', e));
      }
      
      // En mode développement, simuler un succès
      if (process.env.NODE_ENV !== 'production') {
        console.log('🔄 TenantAPI - Mode développement: simulation de définition du tenant par défaut');
        return { 
          success: true, 
          message: 'Tenant défini comme tenant par défaut avec succès (mode développement)' 
        };
      }
      
      // Fournir un message d'erreur convivial
      let errorMessage = 'Échec de la définition du tenant par défaut';
      if (error instanceof AxiosError && error.response) {
        errorMessage = ApiErrorHandler.getErrorMessage(error);
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      return { success: false, message: errorMessage };
    }
  },

  async inviteUserToTenant(tenantId: string, email: string, role?: string): Promise<{ success: boolean; message: string }> {
    // Validation des paramètres
    if (!tenantId || !email) {
      console.warn('inviteUserToTenant: paramètres invalides', { tenantId, email });
      return {
        success: false,
        message: 'L\'ID du tenant et l\'email sont requis pour inviter un utilisateur'
      };
    }
    try {
      if (!tenantManager.isTenantAccessible(tenantId)) {
        console.warn(`👤 TenantAPI: Tenant non accessible pour invitation: ${tenantId}`);
        return {
          success: false,
          message: `Vous n'avez pas les droits nécessaires pour inviter des utilisateurs dans cette organisation.`
        };
      }

      const endpoint = `tenants/${tenantId}/invite/`;
      const token = await import('../auth/token-manager').then(mod => mod.TokenManager.getAccessToken() || '');
      const response = await axiosInstance.post(endpoint, { email, role: role || 'user', send_email: true }, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        withCredentials: true,
      });
      
      // Enregistrer le succès de la requête
      ApiErrorHandler.getInstance().recordApiSuccess(endpoint, response.status);
      
      const result = response.data;
      await tenantAuditService.logAction({
        tenantId,
        userId: 'current-user',
        action: AuditActionType.CREATE,
        resource: 'tenant_invitation',
        resourceId: tenantId,
        status: 'success',
        details: { email, role },
      });
      return { success: true, message: result.message || 'User invited successfully' };
    } catch (error: unknown) {
      console.error('Error inviting user:', error);
      
      // Enregistrer l'erreur
      if (error instanceof AxiosError) {
        const endpoint = `tenants/${tenantId}/invite/`;
        ApiErrorHandler.getInstance().recordApiError(error);
        
        // Journaliser l'échec de l'invitation
        await tenantAuditService.logAction({
          tenantId,
          userId: 'current-user',
          action: AuditActionType.CREATE,
          resource: 'tenant_invitation',
          resourceId: tenantId,
          status: 'failure',
          details: { email, role, error: error instanceof Error ? error.message : String(error) },
        }).catch(e => console.error('Failed to log audit event:', e));
      }
      
      // En mode développement, créer un utilisateur mocké
      if (process.env.NODE_ENV !== 'production') {
        const mockUser: TenantUser = {
          id: `mock-user-${Date.now()}`,
          email,
          username: email.split('@')[0],
          role: role || 'user',
          status: 'pending',
          invitedAt: new Date().toISOString(),
        };
        this.userCache[tenantId] = {
          users: [...(this.userCache[tenantId]?.users || []), mockUser],
          timestamp: Date.now(),
          isFallback: true,
        };
        return { success: true, message: 'User invited successfully (mock)' };
      }
      
      // Fournir un message d'erreur convivial
      let errorMessage = 'Failed to invite user';
      if (error instanceof AxiosError && error.response) {
        errorMessage = ApiErrorHandler.getErrorMessage(error);
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      return { success: false, message: errorMessage };
    }
  },

  async getTenantUsers(tenantId: string): Promise<TenantUser[]> {
    if (!tenantId) {
      console.warn('getTenantUsers: tenantId is required');
      return [];
    }

    // Check cache first
    const cachedEntry = this.userCache[tenantId];
    if (cachedEntry && (Date.now() - cachedEntry.timestamp < this.CACHE_TTL)) {
      console.log(`TenantAPI: Returning cached users for tenant ${tenantId}`);
      return cachedEntry.users;
    }

    try {
      if (!tenantManager.isTenantAccessible(tenantId)) {
        console.warn(`👤 TenantAPI: Tenant not accessible for getTenantUsers: ${tenantId}`);
        // Fallback to mock data in dev if tenant is not accessible but we still want to show something
        if (process.env.NODE_ENV !== 'production') {
          const mockUsers: TenantUser[] = [
            { id: 'mock-user-1', email: 'user1@example.com', username: 'user1', role: 'admin', status: 'active' },
            { id: 'mock-user-2', email: 'user2@example.com', username: 'user2', role: 'member', status: 'active' },
          ];
          this.userCache[tenantId] = { users: mockUsers, timestamp: Date.now(), isFallback: true };
          return mockUsers;
        }
        return [];
      }

      const endpoint = `tenants/${tenantId}/users/`;
      const token = await getAuthToken();
      const response = await axiosInstance.get(endpoint, {
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        withCredentials: true,
      });

      ApiErrorHandler.getInstance().recordApiSuccess(endpoint, response.status);
      const users: TenantUser[] = response.data.users || response.data; // Adjust based on actual API response structure
      
      this.userCache[tenantId] = { users, timestamp: Date.now() };
      return users;
    } catch (error) {
      console.error(`Error fetching users for tenant ${tenantId}:`, error);
      ApiErrorHandler.getInstance().recordApiError(error as AxiosError);

      if (process.env.NODE_ENV !== 'production') {
        console.warn(`TenantAPI: Falling back to mock users for tenant ${tenantId} due to error.`);
        const mockUsers: TenantUser[] = [
          { id: 'mock-user-dev-1', email: 'devuser1@example.com', username: 'devuser1', role: 'admin', status: 'active' },
          { id: 'mock-user-dev-2', email: 'devuser2@example.com', username: 'devuser2', role: 'member', status: 'active' },
        ];
        this.userCache[tenantId] = { users: mockUsers, timestamp: Date.now(), isFallback: true };
        return mockUsers;
      }
      return [];
    }
  },

  async updateTenant(tenantId: string, data: Partial<Tenant>): Promise<Tenant | null> {
    if (!tenantId || !data) {
      console.warn('updateTenant: tenantId and data are required');
      return null;
    }

    try {
      if (!tenantManager.isTenantAccessible(tenantId)) {
        console.warn(`👤 TenantAPI: Tenant not accessible for updateTenant: ${tenantId}`);
        // Optionally, allow updates to mock data in dev even if not "accessible"
        if (process.env.NODE_ENV !== 'production') {
          const existingMockTenant = mockTenants.find(t => t.id === tenantId);
          if (existingMockTenant) {
            const updatedTenant = { ...existingMockTenant, ...data, updatedAt: new Date().toISOString() } as Tenant;
            // Update in mockTenants array if it's used globally, or manage a local mock state
            const index = mockTenants.findIndex(t => t.id === tenantId);
            if (index !== -1) mockTenants[index] = updatedTenant;
            console.log(`TenantAPI: Mock updated tenant ${tenantId}`, updatedTenant);
            return updatedTenant;
          }
        }
        return null;
      }

      const endpoint = `tenants/${tenantId}/`;
      const token = await getAuthToken();
      const response = await axiosInstance.put(endpoint, data, { // Or PATCH if your API supports partial updates
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        withCredentials: true,
      });

      ApiErrorHandler.getInstance().recordApiSuccess(endpoint, response.status);
      const updatedTenant: Tenant = response.data.tenant || response.data; // Adjust based on actual API response structure
      
      // Optionally, update local caches or state (e.g., tenantManager)
      tenantManager.updateTenantInCache(updatedTenant);

      await tenantAuditService.logAction({
        tenantId,
        userId: 'current-user', // Replace with actual user ID if available
        action: AuditActionType.UPDATE,
        resource: 'tenant',
        resourceId: tenantId,
        status: 'success',
        details: { updatedFields: Object.keys(data) },
      });

      return updatedTenant;
    } catch (error) {
      console.error(`Error updating tenant ${tenantId}:`, error);
      ApiErrorHandler.getInstance().recordApiError(error as AxiosError);
      await tenantAuditService.logAction({
        tenantId,
        userId: 'current-user',
        action: AuditActionType.UPDATE,
        resource: 'tenant',
        resourceId: tenantId,
        status: 'failure',
        details: { error: error instanceof Error ? error.message : String(error) },
      });

      if (process.env.NODE_ENV !== 'production') {
        console.warn(`TenantAPI: Mock update failed for tenant ${tenantId}, returning partial mock or null.`);
        // Attempt to apply partial update to a mock tenant if found
        const existingMockTenant = mockTenants.find(t => t.id === tenantId);
        if (existingMockTenant) {
          return { ...existingMockTenant, ...data, name: data.name || existingMockTenant.name } as Tenant;
        }
      }
      return null;
    }
  },
};

export default tenantAPI;
